import { useEffect, useRef, useState } from 'react';
import { spore } from '@/spore/runtime';

export const FEELING_COLORS = { curiosity: '#00e5ff', fear: '#ff4d4d', trust: '#8cffc8', joy: '#ffc828', grief: '#7896eb', unity: '#d2aaff' };
const pad = (n) => String(n).padStart(2, '0');

export const TopBar = ({ snap }) => (
  <div className="absolute left-10 right-10 top-8 flex items-start justify-between">
    <div>
      <div data-testid="stage-specimen-label" className="label-eyebrow">[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ]</div>
      <div className="font-display mt-1 text-xl font-bold tracking-tight text-[var(--ink)]">SPORE Language Assessment - Feelings.</div>
      <div data-testid="stage-phase-label" className="mt-2 text-sm tracking-[0.25em] text-[var(--muted)]">
        {snap.phase === 'opening' ? 'INTRODUCTION' : snap.phase === 'finale' ? 'CONCLUSION' : `EXPERIMENT ${pad(snap.chapterIndex + 1)} · ${snap.phase.toUpperCase()}`}
      </div>
    </div>
    <Tracker snap={snap} />
  </div>
);

export const Tracker = ({ snap }) => {
  const count = snap.unlocked.filter(Boolean).length;
  return (
    <div data-testid="assessment-tracker" className="text-right">
      <div className="label-eyebrow mb-2">ASSESSMENT <span data-testid="assessment-count" className="text-[var(--ink)]">{count}/6</span></div>
      <div className="flex gap-2">
        {snap.feelings.map((f, i) => {
          const open = snap.unlocked[i];
          const current = i === snap.chapterIndex && !open && snap.phase !== 'opening';
          return (
            <div key={f} data-testid={`tracker-slot-${i}`} data-unlocked={open ? 'true' : 'false'} className={`min-w-[7.5rem] border px-3 py-2 text-center text-sm tracking-[0.18em] ${current ? 'tracker-current' : ''}`} style={{ borderColor: open ? FEELING_COLORS[f] : 'rgba(255,255,255,0.12)', color: open ? FEELING_COLORS[f] : 'var(--dim)', textShadow: open ? `0 0 14px ${FEELING_COLORS[f]}` : 'none' }}>
              {open ? f.toUpperCase() : <span className="glitch" data-text="?????">?????</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const KEY_PIPS = { fear: 5, trust: 3, joy: 4 };

export const CoherenceMeter = ({ snap }) => {
  const f = snap.feeling;
  const color = (f && FEELING_COLORS[f]) || 'var(--spore)';
  const pct = Math.round(snap.resonance * 100);
  const pips = KEY_PIPS[f];
  return (
    <div data-testid="coherence-meter" data-value={snap.resonance.toFixed(3)} className="stage-panel w-[420px] p-5">
      <div className="flex items-baseline justify-between">
        <span className="label-eyebrow">SIGNAL COHERENCE</span>
        <span data-testid="coherence-value" className="font-display text-4xl font-extrabold tabular-nums" style={{ color, textShadow: `0 0 18px ${color}` }}>{pct}%</span>
      </div>
      <div className="relative mt-3 h-3 border border-white/10 bg-white/5">
        <div className="h-full transition-[width] duration-300" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 18px ${color}` }} />
        <div className="absolute -top-1 h-5 w-px bg-white/60" style={{ left: '70%' }} />
      </div>
      <div className="mt-3 flex items-center justify-between text-xs tracking-[0.2em] text-[var(--muted)]">
        <span data-testid="key-label">{snap.phase === 'discovery' || snap.phase === 'reveal' ? snap.keyLabel : '—'}</span>
        {pips ? (
          <span data-testid="key-progress" data-value={snap.keyProgress.toFixed(2)} className="flex gap-1.5">
            {Array.from({ length: pips }, (_, i) => (
              <i key={i} className="inline-block h-2.5 w-2.5 border" style={{ borderColor: color, background: i < Math.round(snap.keyProgress * pips) ? color : 'transparent', opacity: snap.keyArmed ? 1 : 0.35 }} />
            ))}
          </span>
        ) : f === 'unity' ? (
          <span data-testid="key-progress" data-value={snap.keyProgress.toFixed(2)} className="flex gap-3">
            {['CONVERGE', 'RELEASE', 'KNOCK'].map((s, i) => (
              <span key={s} style={{ color: snap.unityStage > i || snap.phase === 'reveal' ? color : snap.unityStage === i ? 'var(--ink)' : 'var(--dim)' }}>{s}</span>
            ))}
          </span>
        ) : (
          <span data-testid="key-progress" data-value={snap.keyProgress.toFixed(2)}>{f ? 'FULL RESONANCE' : ''}</span>
        )}
      </div>
    </div>
  );
};

export const Transmission = ({ tx }) => {
  const [typed, setTyped] = useState({ at: null, n: 0 });
  const at = tx ? tx.startedAt : null;
  // Count belongs to one transmission; a new line renders empty until its own typewriter starts.
  const shown = !tx ? 0 : typed.at === at ? typed.n : tx.ended ? tx.text.length : 0;
  useEffect(() => {
    if (!tx) return undefined;
    const len = tx.text.length;
    setTyped({ at, n: tx.ended ? len : 0 });
    const step = Math.max(18, Math.min(60, (tx.durationMs * 0.8) / Math.max(1, len)));
    const id = setInterval(() => setTyped((p) => (p.at !== at || p.n >= len ? p : { at, n: p.n + 1 })), step);
    return () => clearInterval(id);
  }, [at]); // eslint-disable-line react-hooks/exhaustive-deps
  const typing = tx && shown < tx.text.length;
  return (
    <div data-testid="transmission-panel" className={`stage-panel w-[min(760px,46vw)] p-5 transition-opacity duration-1000 ${tx && !tx.ended ? 'opacity-100' : tx ? 'opacity-60' : 'opacity-30'}`}>
      <div className="mb-2 flex justify-between text-xs tracking-[0.3em]" style={{ fontFamily: 'var(--font-mono)' }}>
        <span className="text-[var(--amber)]">PR. H. CHAMPIGNON · MYCOLOGIST {typing ? '' : tx ? '' : '…'}</span>
        {tx && tx.voiceSource === 'openai' && <span data-testid="transmission-fallback-voice" className="text-[var(--amber)]">BACKUP VOICE</span>}
        {tx && tx.mode === 'text' && <span data-testid="transmission-text-only" className="text-[var(--amber)]">SUBTITLES ONLY</span>}
      </div>
      <p data-testid="transmission-text" data-id={tx ? tx.id : ''} className="font-body min-h-[4.2rem] text-2xl font-semibold leading-snug text-[var(--ink)] lg:text-3xl">
        {tx ? tx.text.slice(0, shown) : ''}
        {typing && <span className="caret">▌</span>}
      </p>
    </div>
  );
};

export const MiniScope = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext('2d');
    let raf;
    const loop = () => {
      const w = (c.width = c.clientWidth);
      const h = (c.height = c.clientHeight);
      const now = spore.now();
      const x = (t) => w - ((now - t) / 6000) * w;
      ctx.strokeStyle = '#00e5ff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      spore.scope.cc.forEach((p, i) => {
        if (p.v === null) return;
        const y = h - 4 - (p.v / 127) * (h - 8);
        if (i === 0) ctx.moveTo(x(p.t), y);
        else ctx.lineTo(x(p.t), y);
      });
      ctx.stroke();
      spore.scope.notes.forEach((n) => {
        ctx.fillStyle = n.v > 90 ? '#ffb000' : '#00ff9d';
        ctx.fillRect(x(n.t), h - (n.v / 127) * h, 2, (n.v / 127) * h);
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div className="stage-panel w-[300px] p-3">
      <div className="label-eyebrow mb-1">BIO-SIGNAL · CC90</div>
      <canvas ref={ref} data-testid="stage-mini-scope" className="h-[80px] w-full" />
    </div>
  );
};

export const ProtocolPanel = ({ snap }) => {
  if (!snap.experiment) return <div className="w-[420px]" />;
  const color = FEELING_COLORS[snap.feeling];
  const active = snap.phase === 'active';
  return (
    <div data-testid="protocol-panel" className="stage-panel w-[420px] p-5">
      <div className="label-eyebrow mb-3">PROTOCOL · EXPERIMENT {pad(snap.chapterIndex + 1)}/06</div>
      {snap.steps.map((st, i) => {
        const done = i < snap.stepIndex || (i === snap.stepIndex && snap.stepProgress >= 1);
        const cur = i === snap.stepIndex && !done;
        return (
          <div key={i} data-testid={`protocol-step-${i}`} data-state={done ? 'done' : cur ? 'current' : 'todo'} className="flex items-center gap-3 py-1 text-base tracking-[0.12em]" style={{ color: done ? color : cur ? 'var(--ink)' : 'var(--dim)' }}>
            <span className="inline-block w-5">{done ? '✓' : cur ? '▸' : '·'}</span>
            <span>STEP {i + 1}/{snap.steps.length} · {st.label}</span>
            {cur && active && st.kind === 'STILL' && <span data-testid="protocol-still-timer" className="ml-auto tabular-nums">{(snap.stepProgress * st.seconds).toFixed(1)} s</span>}
            {cur && !active && <span className="ml-auto text-xs text-[var(--muted)]">LISTEN…</span>}
            {cur && active && st.kind !== 'STILL' && <span className="ml-auto text-xs dot-pulse" style={{ color }}>NOW</span>}
          </div>
        );
      })}
      <div data-testid="touch-indicator" data-active={snap.touch.touchActive ? 'true' : 'false'} className="mt-3 text-xs tracking-[0.25em]" style={{ color: snap.touch.touchActive ? color : 'var(--dim)' }}>
        {snap.touch.touchActive ? `● CONTACT ${(snap.touch.touchMs / 1000).toFixed(1)} s` : '○ BASE STATE · FLUTTER'}
      </div>
    </div>
  );
};

export const AudienceHud = ({ snap }) => (
  <div data-testid="audience-hud" className="pointer-events-none absolute inset-0">
    <TopBar snap={snap} />
    <div className="absolute bottom-8 left-10 right-10 flex items-end justify-between gap-6">
      <ProtocolPanel snap={snap} />
      <Transmission tx={snap.ui.transmission} />
      <MiniScope />
    </div>
  </div>
);
